import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { fetchInsight, fetchInsights } from "@/lib/api";

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchInsight(slug)
      .then((p) => {
        setPost(p);
        document.title = `${p.title} | Track Digital`;
      })
      .catch(() => setNotFound(true));
    fetchInsights().then((all) => setRelated(all.filter((p) => p.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-24 td-container text-center" data-testid="insight-not-found">
        <h1 className="text-3xl font-semibold mb-4">Article not found</h1>
        <Link to="/insights" className="text-[#7C6CF0] font-medium">Back to Insights</Link>
      </main>
    );
  }

  if (!post) {
    return <main className="pt-40 pb-24 td-container"><p className="text-[#8a8a8a]">Loading...</p></main>;
  }

  return (
    <main data-testid="insight-detail-page">
      <article className="pt-36 md:pt-44 pb-16">
        <div className="td-container max-w-3xl">
          <Reveal>
            <Link to="/insights" className="inline-flex items-center gap-2 text-[#8a8a8a] hover:text-black transition-colors mb-8 text-sm">
              <ArrowLeft size={16} /> All insights
            </Link>
            <span className="td-label block mb-5">{post.category} · {post.read_time}</span>
            <h1 className="text-3xl md:text-5xl font-semibold leading-[1.08] mb-6">{post.title}</h1>
            <p className="text-lg text-[#4a4a4a] leading-relaxed">{post.excerpt}</p>
            <div className="flex items-center gap-3 mt-8 pb-8 border-b border-[#eee]">
              <div className="w-10 h-10 rounded-full bg-[#7C6CF0] text-white flex items-center justify-center font-head font-semibold text-sm">
                DB
              </div>
              <div>
                <p className="font-medium text-black text-sm">{post.author}</p>
                <p className="text-[#8a8a8a] text-xs">Founder, Track Digital</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="td-container max-w-4xl mt-10">
          <Reveal>
            <div className="aspect-[16/9] rounded-3xl overflow-hidden">
              <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>

        <div className="td-container max-w-3xl mt-12">
          <div className="prose-td">
            {(() => {
              // --- Inline markdown parser: **bold**, *italic*, _italic_ ---
              const renderInline = (text) => {
                if (typeof text !== "string" || !text) return text;
                const re = /(\*\*[^*\n]+?\*\*)|(\*[^*\s][^*\n]*?[^*\s]?\*)|(_[^_\s][^_\n]*?[^_\s]?_)/g;
                const nodes = [];
                let last = 0;
                let m;
                let k = 0;
                while ((m = re.exec(text)) !== null) {
                  if (m.index > last) nodes.push(text.slice(last, m.index));
                  if (m[1]) {
                    nodes.push(<strong key={`b${k}`} className="font-semibold text-black">{m[1].slice(2, -2)}</strong>);
                  } else if (m[2]) {
                    nodes.push(<em key={`i${k}`}>{m[2].slice(1, -1)}</em>);
                  } else if (m[3]) {
                    nodes.push(<em key={`i${k}`}>{m[3].slice(1, -1)}</em>);
                  }
                  k++;
                  last = re.lastIndex;
                }
                if (last < text.length) nodes.push(text.slice(last));
                return nodes.length > 0 ? nodes : text;
              };

              return post.body
                .replace(/\r\n/g, "\n")
                .split(/\n{2,}/)
                .map((block, i) => {
                  const b = block.trim();
                  if (!b) return null;

                  // Headings — support ## / ### with or without trailing space
                  if (b.startsWith("###")) {
                    const text = b.replace(/^###\s*/, "");
                    return (
                      <h3 key={i} className="font-head text-xl md:text-2xl font-semibold text-black mt-10 mb-3 leading-tight">
                        {renderInline(text)}
                      </h3>
                    );
                  }
                  if (b.startsWith("##")) {
                    const text = b.replace(/^##\s*/, "");
                    return (
                      <h2 key={i} className="font-head text-2xl md:text-3xl font-semibold text-black mt-12 mb-4 leading-tight">
                        {renderInline(text)}
                      </h2>
                    );
                  }

                  // Bullets — support *, -, •, and Unicode bullet chars
                  const lines = b.split("\n");
                  const bulletRe = /^([*\-•·●◦▪]|[*\-]{2})\s+/;
                  if (lines.length > 0 && lines.every((l) => bulletRe.test(l.trim()))) {
                    return (
                      <ul key={i} className="list-disc pl-6 space-y-2 mb-6 text-lg text-[#333] leading-[1.7]">
                        {lines.map((l, j) => (
                          <li key={j}>{renderInline(l.trim().replace(bulletRe, ""))}</li>
                        ))}
                      </ul>
                    );
                  }

                  // Paragraph
                  return (
                    <p key={i} className="text-lg text-[#333] leading-[1.8] mb-6">{renderInline(b)}</p>
                  );
                });
            })()}
          </div>

          <div className="mt-12 p-8 rounded-3xl bg-[#fafafa] border border-[#eee] flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#7C6CF0] text-white flex items-center justify-center font-head font-semibold shrink-0">
              DB
            </div>
            <div>
              <p className="font-head font-semibold text-black mb-1">David Ball</p>
              <p className="text-[#6b6b6b] text-[15px] leading-relaxed">
                Founder of Track Digital, helping founder-led and purpose-led businesses bring clarity,
                structure and consistency to their marketing.
              </p>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-16 bg-[#fafafa]">
          <div className="td-container">
            <span className="td-label block mb-8">Related Posts</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link key={p.slug} to={`/insights/${p.slug}`} className="group block">
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-4 border border-[#eee]">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                  </div>
                  <span className="td-label block mb-2">{p.category}</span>
                  <h3 className="text-lg font-semibold leading-snug group-hover:text-[#7C6CF0] transition-colors inline-flex items-start gap-1">
                    {p.title} <ArrowUpRight size={16} className="mt-1 shrink-0 text-[#c0c0c0] group-hover:text-[#7C6CF0]" />
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection
        label="Work With Us"
        heading="Ready to bring this thinking into your business?"
        copy="Book a discovery call and we will talk through the clearest way forward for your marketing."
      />
    </main>
  );
}
